import { GuardedDeletionService } from 'projects/orchestrator/src/lib/services/guarded-deletion.service';
import { OnInit, Component, inject } from '@angular/core';
import { PipelinePlannedPlantationHeaderService } from '../proxy/operational/controllers';
import { ListService } from '@abp/ng.core';
import {
  PipelinePlannedPlantationHeaderCreateDto,
  PipelinePlannedPlantationHeaderUpdateDto,
} from '../proxy/operational/dtos/pipeline-planned-plantation-header';
import { PIPELINEPLANNEDPLANTATIONHEADER_ACTION_IDS, PIPELINEPLANNEDPLANTATIONHEADER_CONFIG, PIPELINEPLANNEDPLANTATIONHEADER_FIELDS } from './pipeline-planned-plantation-header.config';
import { BaseHierarchicalCrudComponent, DEFAULT_TOASTER_LIFE, GenericForm, IActionsSchema, ICrudService, localizeWithFallback, resolveActions, ZnHierarchicalViewComponent } from '@zendra/shared';
import { CommonModule } from '@angular/common';
import { Confirmation, ConfirmationService } from '@abp/ng.theme.shared';
import { PipelinePlannedPlantationHeaderPlanningOrchestratorService } from 'orchestrator';
import {
  GetPipelinePlannedPlantationHeaderProjectionListInputDto,
  PipelinePlannedPlantationHeaderProjectionDto,
  PipelinePlannedPlantationHeaderProjectionExcelDownloadDto,
} from 'projects/orchestrator/src/lib/proxy/upm/zendra/cms/projection/pipeline-planned-plantation-headers/models';

@Component({
  selector: 'app-pipeline-planned-plantation-header',
  imports: [CommonModule, GenericForm, ZnHierarchicalViewComponent],
  providers: [ListService, PipelinePlannedPlantationHeaderService, ConfirmationService],
  templateUrl: './pipeline-planned-plantation-header.html',
  styleUrl: './pipeline-planned-plantation-header.scss',
})
export class PipelinePlannedPlantationHeader extends BaseHierarchicalCrudComponent<
  PipelinePlannedPlantationHeaderProjectionDto,
  PipelinePlannedPlantationHeaderCreateDto,
  PipelinePlannedPlantationHeaderUpdateDto,
  PipelinePlannedPlantationHeaderProjectionExcelDownloadDto
> implements OnInit {
  private readonly guardedDeletion = inject(GuardedDeletionService);
  operationalService = inject(PipelinePlannedPlantationHeaderService);
  orchestratorService = inject(PipelinePlannedPlantationHeaderPlanningOrchestratorService);

  service: ICrudService<
    PipelinePlannedPlantationHeaderProjectionDto,
    PipelinePlannedPlantationHeaderCreateDto,
    PipelinePlannedPlantationHeaderUpdateDto,
    PipelinePlannedPlantationHeaderProjectionExcelDownloadDto
  > = {
    create: input => this.operationalService.create(input),
    update: (id, input) => this.operationalService.update(id, input),
    projectionConsistency: this.createProjectionConsistency(
      this.orchestratorService,
      'pipelinePlannedPlantationHeaderId',
    ),
    deleteByIds: ids => this.guardedDeletion.deleteByIds('pipeline-planned-plantation-headers', ids),
    getList: (query: GetPipelinePlannedPlantationHeaderProjectionListInputDto) =>
      this.orchestratorService.getList(query),
    getDownloadToken: () => this.orchestratorService.getDownloadToken(),
    getListAsExcelFile: input => this.orchestratorService.getListAsExcelFile(input),
  };

  config = PIPELINEPLANNEDPLANTATIONHEADER_CONFIG;
  fields = PIPELINEPLANNEDPLANTATIONHEADER_FIELDS;
  actions: IActionsSchema | null = null;

  ngOnInit(): void {
    this.actions = resolveActions(this.config.actions, {
      [PIPELINEPLANNEDPLANTATIONHEADER_ACTION_IDS.removeCascade]:
        () => this.removeCascade(),
    });
  }

  protected prepareUpdateDto(
    data: any,
    selected: PipelinePlannedPlantationHeaderProjectionDto,
  ): PipelinePlannedPlantationHeaderUpdateDto {
    return {
      vendorId: data.vendorId,
      pipelinePropertyId: data.pipelinePropertyId,
      concurrencyStamp: selected.concurrencyStamp,
    };
  }

  protected getEntityName(): string {
    return 'PipelinePlannedPlantationHeader';
  }

  private removeCascade(): void {
    if (this.selectedRows.length !== 1) {
      return;
    }

    const [id] = this.selectedRows;
    const selected = this.dataset().find(item => item.id === id);
    if (!id || !selected?.concurrencyStamp) {
      return;
    }

    this.confirmationService.show(
      localizeWithFallback(
        this.localizationService,
        'Operational::Action.Trigger.RemoveCascadeFromPipelinePlanConfirmationMessage',
      ),
      localizeWithFallback(
        this.localizationService,
        'Operational::Action.Trigger.RemoveCascadeFromPipelinePlanConfirmationTitle',
      ),
      'error',
      {
        hideCancelBtn: false,
        yesText: localizeWithFallback(
          this.localizationService,
          'LanguageService::Confirm',
        ),
        cancelText: localizeWithFallback(
          this.localizationService,
          'LanguageService::Cancel',
        ),
        iconTemplate: '<i class="bi bi-exclamation-octagon-fill"></i>',
      },
    ).subscribe(status => {
      if (status !== Confirmation.Status.confirm) {
        return;
      }

      this.loading.set(true);
      this.orchestratorService.removeCascade(id, {
        concurrencyStamp: selected.concurrencyStamp!,
      }).subscribe({
        next: () => {
          this.toaster.success('LanguageService::Actions:SuccessMessage', '', {
            life: DEFAULT_TOASTER_LIFE,
            messageLocalizationParams: [
              this.localizationService.instant('Operational::Action.Trigger.RemoveCascadeFromPipelinePlan'),
            ],
          });
          this.clearSelection();
          this.refreshGrid();
        },
        error: () => {
          this.loading.set(false);
          this.toaster.error(
            'Operational::Action.Trigger.RemoveCascadeFromPipelinePlanProcessingError',
            '',
            { life: DEFAULT_TOASTER_LIFE },
          );
        },
      });
    });
  }

}
